/**
 * Modal for leaderboard celebration (confetti) or motivation prediction.
 * Includes option to suppress for this tournament.
 */

import React, { useEffect } from 'react';
import { useLeaderboardCelebration } from '../contexts/LeaderboardCelebrationContext';
import { useAuth } from '../contexts/AuthContext';
import { logUserActivity } from '../services/auditLogService';
import { formatScheduledDateTimeInTimezone, DEFAULT_TIMEZONE } from '../utils/dateTimeUtils';
import './LeaderboardCelebrationModal.css';

export default function LeaderboardCelebrationModal() {
  const { modalState, suppressForTournament, fireConfetti, dismissModal } = useLeaderboardCelebration();
  const { currentUser, userProfile } = useAuth();
  const userTimezone = userProfile?.timezone || DEFAULT_TIMEZONE;

  useEffect(() => {
    if (modalState?.type === 'confetti') {
      fireConfetti();
    }
  }, [modalState?.type, fireConfetti]);

  if (!modalState) return null;

  const { type, tournamentId, tournamentName, rank, ptsToThird, nextMatch, mode } = modalState;
  const isConfetti = type === 'confetti';

  const handleSuppress = () => {
    if (tournamentId) {
      suppressForTournament(tournamentId);
      logUserActivity(
        { userId: currentUser?.uid, userEmail: currentUser?.email, displayName: currentUser?.displayName || currentUser?.email },
        'leaderboard_suppress',
        '/',
        'Suppress from modal',
        { tournamentId }
      );
    }
    dismissModal();
  };

  return (
    <div className="leaderboard-celebration-overlay" role="dialog" aria-modal="true" aria-labelledby="celebration-title">
      <div className="leaderboard-celebration-modal card-ui">
        {isConfetti ? (
          <>
            <div className="leaderboard-celebration-icon" aria-hidden>🏆</div>
            <h2 id="celebration-title" className="leaderboard-celebration-title">
              {rank === 1 && 'You\'re #1!'}
              {rank === 2 && '2nd Place!'}
              {rank === 3 && '3rd Place!'}
              {rank >= 4 && rank <= 10 && `Top 10 – Rank #${rank}!`}
            </h2>
            <p className="leaderboard-celebration-subtitle">
              {tournamentName} · {mode}
            </p>
          </>
        ) : (
          <>
            <div className="leaderboard-celebration-icon leaderboard-celebration-icon-motivation" aria-hidden>🎯</div>
            <h2 id="celebration-title" className="leaderboard-celebration-title">
              You&apos;re rank #{rank}
            </h2>
            <p className="leaderboard-celebration-subtitle">
              {tournamentName} · {mode}
            </p>
            <p className="leaderboard-celebration-motivation">
              Win your next matches to reach top 3!
              {ptsToThird != null && ptsToThird > 0 && (
                <span className="leaderboard-celebration-pts-hint"> (≈{ptsToThird} points to 3rd)</span>
              )}
            </p>
            {nextMatch && (
              <p className="leaderboard-celebration-next-match">
                Next match:{' '}
                {nextMatch.scheduledDate || nextMatch.scheduledAt
                  ? formatScheduledDateTimeInTimezone(
                      typeof (nextMatch.scheduledDate || nextMatch.scheduledAt) === 'number'
                        ? nextMatch.scheduledDate || nextMatch.scheduledAt
                        : (nextMatch.scheduledDate?.toMillis?.() ?? nextMatch.scheduledAt?.toMillis?.() ?? 0),
                      nextMatch.scheduledTime,
                      userTimezone,
                      { useSeparator: ' at ' }
                    )
                  : '—'}
              </p>
            )}
          </>
        )}

        <div className="leaderboard-celebration-actions">
          <button type="button" onClick={dismissModal} className="btn-primary">
            Got it!
          </button>
          <button type="button" onClick={handleSuppress} className="btn-secondary leaderboard-celebration-suppress">
            Don&apos;t show again for this tournament
          </button>
        </div>
      </div>
    </div>
  );
}
